import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { BRAND } from "../theme/index.js";

interface MetricCardProps {
  label: string;
  value: React.ReactNode;
  delta?: string;
  deltaPositive?: boolean;
  icon?: React.ReactNode;
  accent?: "cyan" | "yellow";
  children?: React.ReactNode;
}

/**
 * Metric card with a 3px left-edge accent stripe.
 * cyan  = platform metrics (bots, sessions, chunks)
 * yellow = user-content metrics (messages, documents)
 */
export function MetricCard({
  label,
  value,
  delta,
  deltaPositive,
  icon,
  accent = "cyan",
  children,
}: MetricCardProps) {
  const theme = useTheme();
  const stripeColor = accent === "yellow" ? BRAND.yellow : BRAND.cyan;

  return (
    <Box
      sx={{
        height: "100%",
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderLeft: `3px solid ${stripeColor}`,
        borderRadius: "6px",
        padding: "16px 20px",
        display: "flex",
        flexDirection: "column",
        gap: "6px",
        boxShadow: theme.shadows[1],
        transition: "box-shadow 150ms ease",
        "&:hover": { boxShadow: theme.shadows[2] },
        boxSizing: "border-box",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography
          variant="caption"
          sx={{
            textTransform: "uppercase",
            letterSpacing: "0.6px",
            color: theme.palette.text.secondary,
          }}
        >
          {label}
        </Typography>
        {icon && (
          <Box sx={{ color: stripeColor, opacity: 0.7, display: "flex" }}>
            {icon}
          </Box>
        )}
      </Box>

      <Typography
        sx={{
          fontFamily: "'Outfit', sans-serif",
          fontSize: 28,
          fontWeight: 600,
          lineHeight: 1.1,
          color: theme.palette.text.primary,
        }}
      >
        {value}
      </Typography>

      {delta && (
        <Typography
          variant="caption"
          sx={{ color: deltaPositive ? BRAND.green : BRAND.coral }}
        >
          {delta}
        </Typography>
      )}

      {children}
    </Box>
  );
}
